import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Add, Close } from '@mui/icons-material';
import {
  type Control,
  type FieldArrayWithId,
  type FieldError,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
  Controller,
} from 'react-hook-form';

import { EMPTY_PRODUCT_OPTION } from '../../constants/voucher-product-form.constants';
import type { CreateVoucherRequest } from '../../types';
import { getFieldErrorMessage } from '../../utils/voucher-product-form.utils';
import { ProductPicker } from './product-picker';

interface PurchaseProductsFieldsetProps {
  control: Control<CreateVoucherRequest>;
  fields: FieldArrayWithId<CreateVoucherRequest, 'purchaseProducts', 'id'>[];
  append: UseFieldArrayAppend<CreateVoucherRequest, 'purchaseProducts'>;
  remove: UseFieldArrayRemove;
  fieldErrors?: (
    | FieldError
    | { value?: FieldError; min_quantity?: FieldError }
    | undefined
  )[];
  errorMessage?: string;
  disabled?: boolean;
  isBundling?: boolean;
  onProductChange?: (
    newProduct?: { value: string; label: string; division?: string } | null
  ) => void;
}

export const PurchaseProductsFieldset = ({
  control,
  fields,
  append,
  remove,
  fieldErrors,
  errorMessage,
  disabled = false,
  isBundling = false,
  onProductChange,
}: PurchaseProductsFieldsetProps) => {
  const hasError = Boolean(errorMessage);
  const actionColumnMinWidth = '40px';
  const quantityColumnWidth = { base: '84px', sm: '92px' };
  const showActionColumn = !disabled;

  return (
    <FormControl isInvalid={hasError}>
      <VStack spacing={4} align="stretch">
        <HStack spacing={2} align="center">
          <FormLabel
            fontSize="sm"
            fontWeight="semibold"
            color="gray.700"
            mb={0}
            flex={1}
          >
            Nama produk
          </FormLabel>
          {showActionColumn && <Box minW={actionColumnMinWidth} />}
        </HStack>

        {fields.map((field, index) => {
          const fieldErrorMessage = getFieldErrorMessage(fieldErrors?.[index]);
          const minQuantityErrorMessage = (
            fieldErrors?.[index] as { min_quantity?: FieldError } | undefined
          )?.min_quantity?.message;
          const isLastItem = index === fields.length - 1;
          const showRemoveButton = !disabled && fields.length > 1;
          const showAddButton = !disabled && isLastItem;

          return (
            <Box key={field.id}>
              <HStack spacing={2} align="flex-start" alignItems="center">
                <Box flex={1}>
                  <FormControl isInvalid={Boolean(fieldErrorMessage)}>
                    <Controller
                      control={control}
                      name={`purchaseProducts.${index}` as const}
                      render={({ field: controllerField }) =>
                        disabled ? (
                          <Input
                            value={controllerField.value?.label ?? ''}
                            isDisabled
                            isReadOnly
                          />
                        ) : (
                          <ProductPicker
                            debounce={500}
                            value={
                              controllerField.value?.value
                                ? controllerField.value
                                : null
                            }
                            onChange={(option) => {
                              // Cast to access division from ProductSelectOption
                              const optionWithDivision = option as {
                                value: string;
                                label: string;
                                division?: string;
                              } | null;
                              const nextValue = optionWithDivision
                                ? {
                                    value: optionWithDivision.value,
                                    label: optionWithDivision.label,
                                    division: optionWithDivision.division,
                                    min_quantity:
                                      controllerField.value?.min_quantity ?? 1,
                                  }
                                : {
                                    ...EMPTY_PRODUCT_OPTION,
                                    min_quantity:
                                      controllerField.value?.min_quantity ?? 1,
                                  };

                              controllerField.onChange(nextValue);

                              // Trigger reward product sync when purchase product changes
                              // Pass the new product directly to avoid stale closure
                              if (onProductChange) {
                                onProductChange(optionWithDivision);
                              }
                            }}
                            isDisabled={disabled}
                          />
                        )
                      }
                    />
                    {fieldErrorMessage && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {fieldErrorMessage}
                      </Text>
                    )}
                  </FormControl>
                </Box>

                {!isBundling && (
                  <Box w={quantityColumnWidth}>
                    <FormControl isInvalid={Boolean(minQuantityErrorMessage)}>
                      <Controller
                        control={control}
                        name={`purchaseProducts.${index}.min_quantity` as const}
                        render={({ field: quantityField }) => (
                          <Input
                            type="number"
                            min={1}
                            textAlign="center"
                            value={quantityField.value ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              quantityField.onChange(
                                nextValue === '' ? null : Number(nextValue)
                              );
                            }}
                            isDisabled={disabled}
                          />
                        )}
                      />
                      {minQuantityErrorMessage && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          {minQuantityErrorMessage}
                        </Text>
                      )}
                    </FormControl>
                  </Box>
                )}

                {showActionColumn && (
                  <HStack
                    spacing={1}
                    minW={actionColumnMinWidth}
                    justify="flex-end"
                    flexShrink={0}
                  >
                    {showRemoveButton && (
                      <IconButton
                        aria-label="Hapus produk"
                        icon={<Close fontSize="small" />}
                        size="xs"
                        variant="ghost"
                        color="gray.400"
                        _hover={{ bg: 'gray.100', color: 'gray.600' }}
                        onClick={() => {
                          remove(index);
                          // Trigger reward product sync when purchase product is removed
                          if (onProductChange) {
                            // Use setTimeout to ensure the removal is processed first
                            setTimeout(() => {
                              onProductChange();
                            }, 0);
                          }
                        }}
                      />
                    )}
                    {showAddButton && (
                      <IconButton
                        aria-label="Tambah produk"
                        icon={<Add fontSize="small" />}
                        size="xs"
                        variant="outline"
                        borderColor="blue.500"
                        color="blue.500"
                        borderRadius="md"
                        _hover={{ bg: 'blue.50' }}
                        onClick={() =>
                          append(
                            { ...EMPTY_PRODUCT_OPTION },
                            { shouldFocus: false }
                          )
                        }
                      />
                    )}
                  </HStack>
                )}
              </HStack>
            </Box>
          );
        })}
      </VStack>
      {hasError && <FormErrorMessage mt={2}>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
};
